import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { Building2, LogOut, Menu, X } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { GlobalSearch } from "./GlobalSearch";

export function DashboardLayout({ nav, title, children, bottomNav }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const initials = ((user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")).toUpperCase() || "SJ";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar - desktop */}
      {!bottomNav && (
        <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-slate-950 text-white flex-col z-40">
          <div className="px-6 py-6 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
              <div>
                <div className="font-head font-bold text-base leading-tight">SJ Property Care</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Maintenance Ops</div>
              </div>
            </Link>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {nav.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    active ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white")}>
                  <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="px-3 py-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-sm font-semibold">{initials}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</div>
                <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
              </div>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout} className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
              <LogOut style={{ width: 18, height: 18 }} /> Sign Out
            </button>
          </div>
        </aside>
      )}

      {/* Main */}
      <div className={cn(!bottomNav && "lg:pl-64")}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-200">
          <div className="flex items-center justify-between px-4 md:px-8 h-16">
            <div className="flex items-center gap-3">
              {!bottomNav && (
                <button className="lg:hidden text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
                  {open ? <X /> : <Menu />}
                </button>
              )}
              <h1 className="font-head text-lg md:text-xl font-semibold text-slate-900">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              <GlobalSearch />
              <NotificationBell />
              <span className="hidden lg:block text-sm text-slate-500">{user?.company_name}</span>
              {bottomNav && (
                <button data-testid="logout-btn" onClick={handleLogout} className="text-slate-500 hover:text-rose-600 transition-colors"><LogOut style={{ width: 20, height: 20 }} /></button>
              )}
            </div>
          </div>
          {/* Mobile menu drawer */}
          {open && !bottomNav && (
            <div className="lg:hidden border-t border-slate-200 bg-white px-3 py-3 space-y-1">
              {nav.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100">
                    <Icon style={{ width: 18, height: 18 }} /> {item.label}
                  </Link>
                );
              })}
            </div>
          )}
        </header>

        <main className={cn("px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto", bottomNav && "pb-24")}>{children}</main>
      </div>

      {/* Bottom nav - contractor mobile */}
      {bottomNav && (
        <nav className="fixed bottom-0 inset-x-0 z-50 backdrop-blur-xl bg-white/90 border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-around max-w-lg mx-auto">
            {nav.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to} data-testid={`bottomnav-${item.label.toLowerCase()}`}
                  className={cn("flex flex-col items-center gap-1 py-2.5 px-4 min-w-[64px]", active ? "text-blue-600" : "text-slate-500")}>
                  <Icon style={{ width: 22, height: 22 }} />
                  <span className="text-[11px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
